/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBranchesByCompanyId } from '../../store/slices/branchSlice';
import { fetchCompanyByType } from '../../store/slices/companySlice';

const EmployeeUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  companyId, 
  setCompanyId, 
  preview, 
  setPreview 
}) => {
  const dispatch = useDispatch();
  const { list: companyList = [] } = useSelector((state) => state.companyTypeList || {});
  
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isSeasonal, setIsSeasonal] = useState(false);
  const [importResult, setImportResult] = useState(null); 
  const [isLoading, setIsLoading] = useState(false); 
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchBranchesByCompanyId());
      dispatch(fetchCompanyByType());
      setImportResult(null); 
      setIsLoading(false);
    }
  }, [dispatch, isOpen]);

const handleDownloadTemplate = () => {
  const fileName = "Employee_Upload_Template.xlsx"; // Match the renamed file
  const link = document.createElement("a");
  
  // Use process.env.PUBLIC_URL to ensure it works on subdomains/subfolders
  const publicUrl = process.env.PUBLIC_URL || '';
  link.href = `${publicUrl}/${fileName}`; 
  
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadErrorCSV = () => {
  if (!importResult?.results) return;

  // 1. Define Headers (Added 'Payload')
  const headers = ["Row", "Employee Name", "Employee ID", "Status", "Table", "Error Message", "Payload"];
  
  const rows = importResult.results.map(e => {
    // Stringify the payload and escape double quotes for CSV safety
    const payloadStr = e.payload ? JSON.stringify(e.payload).replace(/"/g, '""') : "";
    
    return [
      e.row,
      `"${e.details?.fullName || 'N/A'}"`,
      `"${e.employeeId || 'N/A'}"`,
      e.status || 'FAILED',
      `"${e.table || 'General'}"`,
      `"${e.message?.replace(/"/g, '""')}"`,
      `"${payloadStr}"` // 2. Add the escaped JSON payload here
    ];
  });

  // 3. Generate CSV Content
  const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  
  link.setAttribute("href", url);
  link.setAttribute("download", `import_report_${importResult.batchId?.substring(0, 8)}.csv`);
  document.body.appendChild(link);
  
  link.click();
  document.body.removeChild(link);
};

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (isLoading) return; 
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (isLoading) return;
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!file || isLoading) return;
    setIsLoading(true);
    try {
      const result = await onUpload(file, isSeasonal);
      if (result) setImportResult(result);
      setFile(null);
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', 
      display: 'flex', alignItems: 'center', justifyContent: 'center', 
      zIndex: 99999, padding: '20px'
    }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spinner-icon { animation: spin 1s linear infinite; display: inline-block; font-size: 1.2rem; }
        .modal-card { background: white; width: 100%; max-width: 500px; border-radius: 20px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden; margin: auto; display: flex; flex-direction: column; max-height: 90vh; }
        .modal-body-scroll { overflow-y: auto; flex: 1; }
        .upload-zone { border: 2px dashed #e2e8f0; border-radius: 12px; padding: 30px; text-align: center; cursor: pointer; transition: all 0.2s; background: #f8fafc; }
        .upload-zone.active { border-color: #3b82f6; background: #eff6ff; }
        .upload-zone.disabled { opacity: 0.6; cursor: not-allowed; }
        .step-label { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: block; }
        .form-select-custom { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; font-size: 0.85rem; width: 100%; outline: none; }
        .btn-main { border-radius: 8px; padding: 12px 20px; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; border: none; }
        .error-log { max-height: 200px; overflow-y: auto; background: #f8fafc; border-radius: 8px; padding: 10px; border: 1px solid #e2e8f0; }
        .btn-text-action { background: none; border: none; padding: 0; color: #3b82f6; font-size: 0.65rem; font-weight: 700; text-decoration: underline; cursor: pointer; }
        .btn-template { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; font-size: 0.7rem; font-weight: 600; padding: 6px 12px; border-radius: 6px; cursor: pointer; transition: all 0.2s; }
        .btn-template:hover { background: #e2e8f0; color: #1e293b; }
        .badge-partial { background: #fff7ed; color: #c2410c; border: 1px solid #ffedd5; font-size: 0.55rem; font-weight: 800; padding: 1px 5px; border-radius: 4px; text-transform: uppercase; margin-left: 8px; }
      `}</style>

      <div className="modal-card border-0 animate__animated animate__zoomIn animate__faster">
        <div className="px-4 py-3 d-flex justify-content-between align-items-center border-bottom flex-shrink-0" 
             style={{ backgroundColor: importResult ? (importResult.stats?.failed > 0 ? '#991b1b' : (importResult.stats?.partial > 0 ? '#d97706' : '#065f46')) : (isSeasonal ? '#065f46' : '#1e293b') }}>
          <div className="d-flex align-items-center text-white">
            <i className={`bi ${isLoading ? 'bi-arrow-repeat spinner-icon' : (importResult ? 'bi-clipboard-check' : (isSeasonal ? 'bi-leaf-fill' : 'bi-file-earmark-arrow-up'))} me-2`}></i>
            <h6 className="m-0 text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
               {isLoading ? 'Uploading...' : (importResult ? 'Process Summary' : (isSeasonal ? 'Import Seasonal Records' : 'Import Employees'))}
            </h6>
          </div>
          {!isLoading && <button className="btn-close btn-close-white" onClick={onClose} style={{ fontSize: '0.7rem' }}></button>}
        </div>

        <div className="p-4 modal-body-scroll">
          {!importResult ? (
            <>
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="step-label m-0">1. Target Company {!isSeasonal && <span className="text-danger">*</span>}</label>
                  <button type="button" className="btn-template d-flex align-items-center" onClick={handleDownloadTemplate}>
                    <i className="bi bi-file-earmark-arrow-down me-1"></i> Download Upload Template
                  </button>
                </div>
                <select className="form-select-custom shadow-none" disabled={isLoading} value={companyId || ''} onChange={(e) => setCompanyId(e.target.value)}>
                  <option value="">-- Select Company --</option>
                  {companyList.map((c) => (<option key={c.id} value={c.id}>{c.companyName}</option>))}
                </select>
              </div>

              <div className="d-flex align-items-center justify-content-between p-2 px-3 rounded-3 mb-3 border border-success border-opacity-25" style={{ backgroundColor: '#f0fdf4' }}>
                <div>
                  <h6 className="m-0 fw-bold text-success" style={{ fontSize: '0.75rem' }}>Seasonal Hire Import</h6>
                  <p className="m-0 text-muted" style={{ fontSize: '0.6rem' }}>Bypass document checks</p>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input shadow-none bg-success border-success" type="checkbox" disabled={isLoading} checked={isSeasonal} onChange={(e) => setIsSeasonal(e.target.checked)} />
                </div>
              </div>

              <div className="mb-4">
                <label className="step-label">2. Upload File</label>
                <div className={`upload-zone ${dragActive ? 'active' : ''} ${isLoading ? 'disabled' : ''}`} 
                     onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} 
                     onClick={() => !isLoading && fileInputRef.current.click()}>
                  <input ref={fileInputRef} type="file" className="d-none" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} />
                  {!file ? (
                    <>
                      <i className="bi bi-cloud-arrow-up-fill text-primary mb-2" style={{ fontSize: '2rem' }}></i>
                      <p className="m-0 fw-bold small">Click or drag file here</p>
                    </>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="bi bi-file-earmark-excel-fill text-success me-2" style={{ fontSize: '1.5rem' }}></i>
                      <span className="fw-bold small text-truncate" style={{ maxWidth: '200px' }}>{file.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-light btn-main w-100 border" onClick={onClose} disabled={isLoading}>Cancel</button>
                <button className="btn btn-primary btn-main w-100" 
                        style={{ backgroundColor: isSeasonal ? '#059669' : '#0f172a' }} 
                        onClick={handleSubmit} disabled={!file || (!isSeasonal && !companyId) || isLoading}>
                  {isLoading ? 'Processing...' : 'Upload Records'}
                </button>
              </div>
            </>
          ) : (
            <div className="animate__animated animate__fadeIn">
              <div className="text-center mb-4">
                <i className={`bi ${importResult.stats?.failed > 0 ? 'bi-exclamation-circle text-danger' : (importResult.stats?.partial > 0 ? 'bi-info-circle text-warning' : 'bi-check-circle text-success')} mb-2`} style={{ fontSize: '2.5rem' }}></i>
                <h6 className="fw-bold m-0">{importResult.message}</h6>
              </div>

              <div className="d-flex justify-content-around bg-light p-3 rounded-3 mb-4 border text-center">
                <div><div className="step-label mb-0">Total</div><div className="fw-bold h5 mb-0">{importResult.stats?.total}</div></div>
                <div><div className="step-label mb-0 text-success">Success</div><div className="fw-bold h5 mb-0 text-success">{importResult.stats?.success}</div></div>
                <div><div className="step-label mb-0 text-warning" style={{ color: '#d97706' }}>Partial</div><div className="fw-bold h5 mb-0" style={{ color: '#d97706' }}>{importResult.stats?.partial || 0}</div></div>
                <div><div className="step-label mb-0 text-danger">Failed</div><div className="fw-bold h5 mb-0 text-danger">{importResult.stats?.failed}</div></div>
              </div>

{importResult.results?.length > 0 && (
  <div className="mb-4 text-start">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <label className="step-label m-0 text-dark">Activity Log</label>
      <button className="btn-text-action" onClick={downloadErrorCSV}>
        <i className="bi bi-download me-1"></i>REPORT
      </button>
    </div>
    <div className="error-log">
      {importResult.results.map((res, index) => {
        // Dynamic styling based on the status from your backend
        const isFailed = res.status === 'FAILED';
        const isPartial = res.status === 'PARTIAL_SUCCESS';
        const isSuccess = res.status === 'SUCCESS';

        const statusColor = isSuccess ? '#059669' : isPartial ? '#c2410c' : '#dc2626';

        return (
          <div key={index} className="mb-2 pb-1 border-bottom border-secondary border-opacity-10">
            <div className="d-flex align-items-center justify-content-between">
              <div className="fw-bold" style={{ fontSize: '0.7rem', color: statusColor }}>
                Row {res.row}: {res.details?.fullName || 'Unknown Employee'}
              </div>
              <div className="d-flex gap-1">
                {isPartial && <span className="badge-partial">Partial</span>}
                {isSuccess && <span className="badge-success-small">Success</span>}
                {isFailed && <span className="badge-failed-small">Failed</span>}
              </div>
            </div>
            <div className="text-muted" style={{ fontSize: '0.65rem' }}>
              <span className="text-uppercase fw-bold" style={{ fontSize: '0.6rem', color: '#6b7280' }}>
                {res.table}:
              </span>{" "}
              {res.message}
              {res.employeeId && res.employeeId !== 'N/A' && (
                <span className="ms-1 text-secondary opacity-75">({res.employeeId})</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
)}
              <button className="btn btn-dark btn-main w-100" onClick={onClose}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeUploadModal;